/* The history rail (board P): a 292px sunken column, „Rozmowy" over „Nowa rozmowa", the grouped
 * conversations, and the retention note at its foot. Every surface is a `--buddy-*` token; the open
 * conversation's row is the raised card, and nothing here is the accent.
 *
 * The rows' secondary lines are set in the secondary ink where the board uses the tertiary one, for
 * the reason the folder's README records: the tertiary ink is not readable text on these grounds. */

.rail {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-14px);
  width: 292px;
  min-height: 0;
  padding: var(--space-4) var(--space-3);
  background: var(--bg-sunken);
  border-right: var(--border-hairline) solid var(--border);
}

.head {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-0) var(--space-6px);
}

.title {
  margin: var(--space-0);
  font-size: var(--fs-foot);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
}

.count {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

.newConversation {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  height: 40px;
  font-family: var(--font-body);
  font-size: var(--fs-foot);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  cursor: pointer;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
  box-shadow: var(--shadow-buddy-control);
}

.newConversation[aria-disabled='true'] {
  cursor: default;
  opacity: 0.4;
}

.newGlyph {
  width: 14px;
  height: 14px;
}

.list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-14px);
  min-height: 0;
}

.scroller {
  overflow-y: auto;
}

.group {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.groupTitle {
  padding: var(--space-6px) var(--space-2) var(--space-2px);
  margin: var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-eyebrow);
  color: var(--text-2);
  text-transform: uppercase;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-0);
  margin: var(--space-0);
  list-style: none;
}

.row {
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
  padding: var(--space-10px);
  color: var(--text);
  text-align: left;
  border-radius: var(--radius-12px);
}

.row:hover {
  background: var(--hover-tint);
}

/* The open conversation: the raised card. Its edge is in the shadow token, so an open row is the
 * same size as a closed one. */
.rowOpen,
.rowOpen:hover {
  background: var(--buddy-raised);
  box-shadow: var(--shadow-buddy-card);
}

.rowTitle {
  overflow: hidden;
  font-size: var(--fs-foot);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowMeta {
  display: flex;
  gap: var(--space-6px);
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
  white-space: nowrap;
}

.rowRecipient {
  overflow: hidden;
  font-weight: var(--fw-bold);
  color: var(--text);
  text-overflow: ellipsis;
}

.rowWaits {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.more,
.noteAction {
  align-self: flex-start;
  padding: var(--space-6px) var(--space-10px);
  font-family: var(--font-body);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
}

.more[aria-disabled='true'] {
  cursor: default;
}

/* The failed and the empty states: centred in the list's own box, so the foot stays where it is. */
.note {
  align-items: center;
  justify-content: center;
  padding: var(--space-0) var(--space-2);
  text-align: center;
}

.noteText {
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.noteAction {
  align-self: center;
}

/* The wait: rows of the list's own shape. */
.skeletonRow {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-10px);
}

.skeletonTitle {
  width: 78%;
  height: 13px;
}

.skeletonMeta {
  width: 46%;
  height: 11px;
}

.foot {
  flex: none;
  padding: var(--space-0) var(--space-2);
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.newConversation:focus-visible,
.row:focus-visible,
.more:focus-visible,
.noteAction:focus-visible,
.scroller:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}
